import { filterByWordPrefixes } from "~/lib/search/word-prefix-search";
import { smartAnsiCompareText } from "~/lib/textComparison/smartAnsiCompareText";
import type { GameAnalysisListItem, GameAnalysisListResponse } from "./contracts";
import { preparationAnalysisOutcomes, type PreparationAnalysisOutcome } from "./metadata";

export type OpeningLookup = ReadonlyMap<number, Readonly<{ problemId: number; title: string; problemSetId: number }>>;

/** One permitted ID lookup shared by presentation, projection and navigation. */
export function buildOpeningLookup(groups: GameAnalysisListResponse["openingGroups"]): OpeningLookup {
  return new Map(groups.flatMap((group) => group.openings.map((opening) => [opening.problemId, { ...opening, problemSetId: group.problemSetId }] as const)));
}

export type ListSort = Readonly<{
  column: "studentColor" | "title" | "uploadedAt" | "studentScore" | "opening" | "analysis";
  direction: "asc" | "desc";
}>;
export type ListDateFilter =
  | Readonly<{ mode: "all" }>
  | Readonly<{ mode: "last-days"; days: number; anchor: Date }>
  | Readonly<{ mode: "interval"; start: string; end: string }>;
export type DateWindow = Readonly<{ startInclusive: number; endExclusive: number }>;

function parseLocalDate(text: string): Date {
  const match = /^([+-]?\d{4,6})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) throw new RangeError("Некорректный период дат");
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(year, month - 1, day);
  if (!Number.isFinite(date.getTime()) || date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) throw new RangeError("Некорректный период дат");
  return date;
}

/** Resolve local calendar boundaries; invalid drafts throw RangeError before changing effective settings. */
export function resolveDateWindow(filter: ListDateFilter): DateWindow | null {
  if (filter.mode === "all") return null;
  if (filter.mode === "interval") {
    const start = parseLocalDate(filter.start);
    const end = parseLocalDate(filter.end);
    if (start.getTime() > end.getTime()) throw new RangeError("Некорректный период дат");
    end.setDate(end.getDate() + 1);
    if (!Number.isFinite(end.getTime())) throw new RangeError("Некорректный период дат");
    return { startInclusive: start.getTime(), endExclusive: end.getTime() };
  }
  if (!Number.isSafeInteger(filter.days) || filter.days <= 0 || !Number.isFinite(filter.anchor.getTime())) throw new RangeError("Некорректный период дат");
  const start = new Date(filter.anchor);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  start.setDate(start.getDate() - filter.days + 1);
  end.setDate(end.getDate() + 1);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) throw new RangeError("Некорректный период дат");
  return { startInclusive: start.getTime(), endExclusive: end.getTime() };
}
export type ListFilters = Readonly<{
  commentPresence?: "all" | "present" | "absent";
  color?: "all" | "unknown" | "white" | "black";
  score?: "all" | "unknown" | 0 | 0.5 | 1;
  analysis?: "all" | "unknown" | PreparationAnalysisOutcome;
  openings?: Readonly<{ ids: readonly number[]; withoutOpening?: boolean; unavailable?: boolean }>;
  showArchived?: boolean;
  favoritesOnly?: boolean;
}>;

function matchesChoice<T extends string | number>(value: T | null, choice: T | "all" | "unknown" | undefined): boolean {
  return choice === undefined || choice === "all" || (choice === "unknown" ? value === null : value === choice);
}

function matchesOpening(game: GameAnalysisListItem, selection: ListFilters["openings"], openings: OpeningLookup): boolean {
  if (!selection || (selection.ids.length === 0 && !selection.withoutOpening && !selection.unavailable)) return true;
  return game.openingId === null ? selection.withoutOpening === true
    : selection.ids.includes(game.openingId) || (selection.unavailable === true && !openings.has(game.openingId));
}

function compareUpload(left: GameAnalysisListItem, right: GameAnalysisListItem, direction: ListSort["direction"]): number {
  return compareKnown(left.uploadedAt === null ? null : Date.parse(left.uploadedAt), right.uploadedAt === null ? null : Date.parse(right.uploadedAt), direction);
}

function compareKnown(left: number | null, right: number | null, direction: ListSort["direction"]): number {
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  return (left - right) * (direction === "asc" ? 1 : -1);
}

function comparePrimary(left: GameAnalysisListItem, right: GameAnalysisListItem, sort: ListSort, openings: OpeningLookup): number {
  switch (sort.column) {
    case "title": return smartAnsiCompareText(left.title, right.title) * (sort.direction === "asc" ? 1 : -1);
    case "studentColor": return compareKnown(
      left.studentColor === null ? null : left.studentColor === "white" ? 0 : 1,
      right.studentColor === null ? null : right.studentColor === "white" ? 0 : 1,
      sort.direction,
    );
    case "studentScore": return compareKnown(left.studentScore, right.studentScore, sort.direction);
    case "analysis": return compareKnown(
      left.analysis === null ? null : preparationAnalysisOutcomes.findIndex(({ code }) => code === left.analysis?.outcome),
      right.analysis === null ? null : preparationAnalysisOutcomes.findIndex(({ code }) => code === right.analysis?.outcome),
      sort.direction,
    );
    case "opening": {
      const a = left.openingId === null ? undefined : openings.get(left.openingId);
      const b = right.openingId === null ? undefined : openings.get(right.openingId);
      const tierA = a ? 0 : left.openingId === null ? 1 : 2;
      const tierB = b ? 0 : right.openingId === null ? 1 : 2;
      return tierA - tierB || (a && b ? smartAnsiCompareText(a.title, b.title) * (sort.direction === "asc" ? 1 : -1) : 0);
    }
    default: return compareUpload(left, right, sort.direction);
  }
}

/** Project normalized games without changing response order or stored values. */
export function projectGameAnalysisList(
  games: readonly GameAnalysisListItem[],
  settings: Readonly<{ query: string; sort: ListSort; openings?: OpeningLookup; filters?: ListFilters; date?: ListDateFilter }>,
): readonly GameAnalysisListItem[] {
  const openings: OpeningLookup = settings.openings ?? new Map();
  const filters = settings.filters;
  const window = resolveDateWindow(settings.date ?? { mode: "all" });
  return filterByWordPrefixes(games, settings.query, (game) => game.title).filter((game) =>
    matchesChoice(game.studentColor, filters?.color) &&
    matchesChoice(game.studentScore, filters?.score) &&
    matchesChoice(game.analysis?.outcome ?? null, filters?.analysis) &&
    matchesOpening(game, filters?.openings, openings) &&
    (filters?.commentPresence !== "present" || game.userComment !== null) &&
    (filters?.commentPresence !== "absent" || game.userComment === null) &&
    (filters?.showArchived !== false || !game.archived) &&
    (!filters?.favoritesOnly || game.favorite) &&
    (window === null || (game.uploadedAt !== null && Date.parse(game.uploadedAt) >= window.startInclusive && Date.parse(game.uploadedAt) < window.endExclusive)),
  ).sort((left, right) => {
    const primary = comparePrimary(left, right, settings.sort, openings);
    return primary || compareUpload(left, right, "desc") || right.problemId - left.problemId;
  });
}
